/**
 * 앱 전역 상태를 관리하는 Provider입니다.
 *
 * 제공 데이터:
 * - appName, appVersion: 앱 정보
 * - me: 로그인된 사용자 정보
 * - pcUid: PC 고유 식별자
 * - license: 라이선스 정보
 * - botId: 현재 봇 ID
 *
 * @remarks
 * tRPC를 통해 백엔드에서 앱 정보와 라이선스 정보를 조회합니다.
 * ErrorBoundary로 하위 컴포넌트 에러를 처리합니다.
 */
import type { AppLicense, AuthData } from '@bit-box/common';
import { createContextProvider } from '../common/create-context';
import { trpc } from '../common/trpc';
import { useSessionStorage } from '../hooks/use-session-storage';
import { App } from 'antd';
import React from 'react';
import { ErrorBoundary } from '../components/error-boundary';
import { useConnectionContext } from './connection-provider';

const [useAppContext, Provider] = createContextProvider<{
  appName?: string;
  appVersion?: string;
  me?: AuthData;
  pcUid?: string;
  license?: AppLicense;
  isPending?: boolean;
  error?: unknown;
  botId: number;
}>();

// let message: MessageInstance
// let notification: NotificationInstance
// let modal: Omit<ModalStaticFunctions, "warn">

/**
 * 앱 전역 상태를 관리하는 Provider.
 * 앱 정보, 사용자 정보, 라이선스 정보를 tRPC를 통해 조회하여 제공합니다.
 * ErrorBoundary로 하위 컴포넌트 에러를 처리합니다.
 */
const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const { electronAppInfo } = useConnectionContext();
  const [botId] = useSessionStorage<number>('botId', electronAppInfo?.botId || 0);

  // 로그인 상태
  const infoQuery = trpc.app.info.useQuery();
  // 라이센스 정보
  const licQuery = trpc.app.license.useQuery();

  // let {message, notification, modal} = App.useApp()

  // console.log(`AppProvider. userId = ${infoQuery.data?.me?.userId}, liveId = ${licQuery.data?.license?.live?.id}`);

  return (
    <App>
      <Provider
        value={{
          appName: infoQuery.data?.appName,
          appVersion: infoQuery.data?.appVersion,
          me: infoQuery.data?.me,
          pcUid: infoQuery.data?.pcUid,
          license: licQuery.data?.license,
          isPending: infoQuery.isPending || licQuery.isPending,
          error: infoQuery.error,
          botId,
        }}
      >
        <ErrorBoundary redirectToPath="/monitor">{children}</ErrorBoundary>
      </Provider>
    </App>
  );
};

export { useAppContext, AppProvider };
